import React, {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
  useCallback
} from 'react'
import {
  Button,
  Input,
  message,
  Drawer,
  Tabs,
  Modal,
  Spin,
  Dropdown,
  Menu,
  Icon
} from 'antd'
import './VideoGeneration.scss'

import StepTitle from '../StepTitle'
import { SettingsContent, AdvancedSettingsContent } from './components/Settings'
import Scene from './components/Scene'
import {
  changeMachineState,
  assembleScenesWithAudio,
  generateTransition,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import ReactPlayer from 'react-player'
import AIVideoModal from './components/FinalVideo'
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  arrayMove
} from 'react-sortable-hoc'
import VideoEditorModal from './components/VideoEditor'
import FinalVideoSelection from './components/FinalVideoSelection/FinalVideoSelection'
import GenerationSummary from './components/GenerationSummary/GenerationSummary'
import BulkClipDownloadModal from './components/BulkClipDownloadModal/BulkClipDownloadModal'
import { LeftOutlined } from '@ant-design/icons'
import GenerationStatusModal from './components/GenerationStatusModal'
import RegenerationInfoModal from './components/RegenerationInfoModal'
import ImageEditorModal from '../ImageGeneration/components/ImageEditorModal'
import StatusView from '../StatusView/StatusView'
import {
  constructAssemblyPayload,
  formatDurationOutput
} from '../../utils/videoUtils'
import { useAutoVideoGeneration } from '../../hooks/useAutoVideoGeneration'
import _ from 'lodash'

const VideoGeneration = forwardRef((props, ref) => {
  const {
    formData,
    setFormData,
    onGoBack,
    videoGenerating,
    generatingVideoForAllScenes,
    handleRegenerateVideo,
    handleStopVideoGeneration,
    handleRetryFailedVideos,
    handleGenerateFullVideo,
    updateShotDataRef,
    videoProvider,
    setScenes,
    isCalculatingDurations,
    calculationProgress,
    onStartDurationCalculation,
    // Final video props
    isGeneratingFinalVideo,
    finalVideoError,
    onGenerateFinalVideo,
    setFinalVideoError,
    // Image generation props
    handleRegenerateImage,
    isImageGenerationLoading,
    isFetchingCharacters,
    videoId,
    onValidationChange
  } = props

  // ===== MAIN STATE =====
  const scenes = formData.shotList || []
  const [transitions, setTransitions] = useState(formData.transitions || {})

  // ===== SCENE PREVIEW CACHE STATE =====
  const [scenePreviewCache, setScenePreviewCache] = useState({})
  // Structure: { sceneId: { videoUrl: 'url', shotVersions: { shotId: vidSrc } } }

  // ===== SETTINGS STATE =====
  const [editingSettings, setEditingSettings] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [advancedSettings, setAdvancedSettings] = useState(null)

  // ===== VALIDATION TRIGGER =====
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(!!formData?.finalVideo)
    }
  }, [formData?.finalVideo, onValidationChange])
  const [advancedSettingsVisible, setAdvancedSettingsVisible] = useState(false)

  // ===== MODAL STATE =====

  const [showVideoEditor, setShowVideoEditor] = useState(false)
  const [showSelectionStep, setShowSelectionStep] = useState(false)
  const [selectedScenesForEditor, setSelectedScenesForEditor] = useState([])
  // generateMainVideo state replaced by isGeneratingFinalVideo prop
  const [showMainVideo, setShowMainVideo] = useState(false)
  // finalVideoError state replaced by finalVideoError prop
  const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false)

  // ===== IMAGE EDITOR STATE =====
  const [showImageEditorModal, setShowImageEditorModal] = useState(false)
  const [imageEditorMode, setImageEditorMode] = useState('nanobanana')
  const [currentEditingShot, setCurrentEditingShot] = useState(null)

  // ===== VIDEO EDITOR STATE =====
  const [editorScenes, setEditorScenes] = useState([])
  const [editorTransitions, setEditorTransitions] = useState({})

  // ===== SCENE PREVIEW STATE =====
  const [showScenePreview, setShowScenePreview] = useState(false)
  const [previewSceneVideo, setPreviewSceneVideo] = useState(null)
  const [scenePreviewError, setScenePreviewError] = useState(null)
  const [shotViewMode, setShotViewMode] = useState('expanded')

  // ===== AUTO VIDEO GENERATION HOOK =====
  // Handles: duration merging, shot detection, auto-regeneration triggering
  const {
    affectedScenesByMismatch,
    showRegenerationInfo,
    setShowRegenerationInfo,
    resetAutoGeneration
  } = useAutoVideoGeneration({
    formData,
    setFormData,
    videoProvider,
    videoGenerating,
    generatingVideoForAllScenes,
    isCalculatingDurations,
    handleGenerateFullVideo,
    debugEnabled: false
  })

  // ===== TRIGGER GENERATION AFTER CALCULATION =====
  const prevIsCalculating = useRef(isCalculatingDurations)
  useEffect(() => {
    if (
      prevIsCalculating.current &&
      !isCalculatingDurations &&
      formData.durationsReady
    ) {
      resetAutoGeneration()
    }
    prevIsCalculating.current = isCalculatingDurations
  }, [isCalculatingDurations, formData.durationsReady, handleGenerateFullVideo])

  // ===== HELPER FUNCTIONS =====
  const generateId = (prefix = '') =>
    `${prefix}${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 8)}`

  // Replace __character1__, __character2__ etc. in prompt with selected character names
  const getFormattedPrompt = prompt => {
    const selectedCharacters = formData.selectedCharacter || []
    const formattedPrompt = selectedCharacters.reduce((acc, char, index) => {
      const placeholder = `__character${index + 1}__`
      return acc.replace(
        new RegExp(placeholder, 'g'),
        char.current.replace(/__/g, '')
      )
    }, prompt)
    return formattedPrompt
  }

  // formatDurationOutput moved to utils/videoUtils.js

  // constructAssemblyPayload moved to utils/videoUtils.js
  const handleConstructAssemblyPayload = (targetData, isFinalVideo) => {
    return constructAssemblyPayload(
      targetData,
      formData.locationMap,
      isFinalVideo,
      transitions // Pass local transitions state or formData.transitions
    )
  }

  const invalidateScenePreview = sceneId => {
    setScenePreviewCache(prev => {
      const newCache = { ...prev }
      delete newCache[sceneId]
      return newCache
    })
  }

  // ===== VIDEO TASK POLLING =====
  async function waitForVideoTaskSuccess(taskId, onProgress) {
    try {
      if (!taskId) return null
      const delayMs = 10000

      while (true) {
        try {
          const statusResponse = await getTaskStatus(taskId)
          const data = statusResponse?.data || null
          const status = data?.status || null

          if (data?.progress && onProgress) {
            onProgress(data.progress)
          }

          if (status === 'success') {
            return data?.output || ''
          }
          if (status === 'failed' || status === 'error') {
            return null
          }
          if (
            status === 'pending' &&
            (data?.progress === '100%' || data?.progress === 100)
          ) {
            // If pending but progress is 100%, retry immediately/quickly
            await new Promise(resolve => setTimeout(resolve, 1000))
            continue
          }
        } catch (err) {
          console.error('Error fetching video task status, retrying:', err)
          return null
        }
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    } catch (err) {
      return null
    }
  }

  const handleGenerateFinalVideoWrapper = async (orderedClips = null) => {
    setShowVideoEditor(false)
    setShowMainVideo(false)
    // isGeneratingFinalVideo prop will handle the loading state
    onGenerateFinalVideo(orderedClips)
  }

  // ===== SCENE PREVIEW HANDLER =====
  const handlePreviewScene = async sceneId => {
    const selectedScene = scenes.find(s => s.id === sceneId)
    if (!selectedScene) return

    const allShotsHaveVideos = selectedScene.shots.every(
      shot => shot.vidSrc || (shot.is_static && shot.imageSrc)
    )
    if (!allShotsHaveVideos) {
      message.warning('Not all shots in this scene have generated videos yet.')
      return
    }

    // Check if we have a valid cached preview
    const cachedPreview = scenePreviewCache[sceneId]
    if (cachedPreview?.videoUrl && cachedPreview?.shotVersions) {
      // Verify all shots match the cached versions
      const allShotsMatch = selectedScene.shots.every(
        shot =>
          cachedPreview.shotVersions[shot.id] ===
          (shot.is_static ? shot.imageSrc : shot.vidSrc)
      )

      if (allShotsMatch) {
        // Use cached preview
        setPreviewSceneVideo(cachedPreview.videoUrl)
        setShowScenePreview(true)
        message.success('Loaded cached scene preview')
        return
      }
    }

    try {
      const filtered = {}
      Object.keys(transitions || {}).forEach(key => {
        if (key.startsWith(`${sceneId}-`)) {
          filtered[key] = transitions[key]
        }
      })

      setShowScenePreview(true)
      setPreviewSceneVideo(null)
      setScenePreviewError(null)

      // We only want to generate preview for the selected scene
      const payload = handleConstructAssemblyPayload([selectedScene], false)
      if (videoId) {
        payload.video_id = videoId
      }
      payload.shot = null
      payload.character_map = formData?.characterMap || null
      payload.outfit_map = formData?.outfitMap || null
      payload.wardrobe = formData?.wardrobe || null
      payload.outfits_reference = formData?.outfits_reference || null

      const response = await assembleScenesWithAudio(payload)
      const taskId = response?.data?.task_id
      const output = await waitForVideoTaskSuccess(taskId)
      const finalVideos = output?.final_videos || {}
      const finalVideo = finalVideos.full_video || Object.values(finalVideos)[0]

      if (finalVideo) {
        setPreviewSceneVideo(finalVideo)

        // Cache the preview with shot versions
        const shotVersions = {}
        selectedScene.shots.forEach(shot => {
          shotVersions[shot.id] = shot.is_static ? shot.imageSrc : shot.vidSrc
        })

        setScenePreviewCache(prev => ({
          ...prev,
          [sceneId]: {
            videoUrl: finalVideo,
            shotVersions
          }
        }))

        message.success('Scene preview generated successfully!')
      } else {
        throw new Error('Scene preview generation failed')
      }
    } catch (error) {
      console.error('Error generating scene preview:', error)
      setScenePreviewError(error?.message || 'Failed to generate scene preview')
    }
  }

  // ===== SETTINGS HANDLERS =====
  const handleEditSettings = (sceneId, shotId) => {
    setEditingSettings({ sceneId, shotId })
    setDrawerVisible(true)
  }

  const handleRegenerateWithReferenceNanoBanana = useCallback(
    (sceneId, shotId) => {
      setCurrentEditingShot({ sceneId, shotId })
      setImageEditorMode('nanobanana')
      setShowImageEditorModal(true)
    },
    []
  )

  const handleImageEditorConfirm = imageUrl => {
    if (!currentEditingShot || !imageUrl) return

    const { sceneId, shotId } = currentEditingShot

    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId
                ? {
                    ...shot,
                    imageSrc: imageUrl,
                    images_history: [
                      ...(shot.images_history || []),
                      { url: imageUrl, timestamp: Date.now() }
                    ]
                  }
                : shot
            )
          }
        }
        return scene
      })
    )
    setShowImageEditorModal(false)
    setCurrentEditingShot(null)
  }

  const handleAdvancedSettings = (sceneId, shotId) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene.shots.find(sh => sh.id === shotId)

    setAdvancedSettings({
      sceneId,
      shotId,
      characterId: shot.characterId || formData?.characterList?.[0]?.id || null,
      shotType: shot.shotType || 'Medium Shot',
      imageSrc: shot.imageSrc || null,
      prompt: shot.prompt || '',
      ...shot
    })
    setAdvancedSettingsVisible(true)
  }

  const updateSettings = (key, value) => {
    if (!editingSettings) return
    const { sceneId, shotId } = editingSettings
    setScenes(prevScenes =>
      prevScenes.map(scene =>
        scene.id === sceneId
          ? {
              ...scene,
              shots: scene.shots.map(shot =>
                shot.id === shotId ? { ...shot, [key]: value } : shot
              )
            }
          : scene
      )
    )
  }

  const updateAdvancedSettings = (key, value) => {
    setAdvancedSettings(prev => ({ ...prev, [key]: value }))
  }

  const saveSettings = (updatedFields = null) => {
    Modal.confirm({
      title: 'Confirm Changes',
      content:
        'Are you sure you want to save these settings? This will regenerate the video.',
      centered: true,
      onOk: () => {
        if (updatedFields && editingSettings) {
          const { sceneId, shotId } = editingSettings
          setScenes(prevScenes =>
            prevScenes.map(scene =>
              scene.id === sceneId
                ? {
                    ...scene,
                    shots: scene.shots.map(shot =>
                      shot.id === shotId ? { ...shot, ...updatedFields } : shot
                    )
                  }
                : scene
            )
          )
        }
        setDrawerVisible(false)
        setEditingSettings(null)

        message.info('Video is regenerating...')
        const { sceneId, shotId } = editingSettings
        handleRegenerateVideo(sceneId, shotId)
      }
    })
  }

  const saveAdvancedSettings = () => {
    const { sceneId, shotId, ...updatedFields } = advancedSettings

    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId
                ? {
                    ...shot,
                    ...updatedFields
                  }
                : shot
            )
          }
        }
        return scene
      })
    )
    setAdvancedSettingsVisible(false)
    message.success('Advanced settings updated successfully!')
  }

  // ===== TRANSITION HANDLERS =====
  // transitionData can be: { transition, duration } or null (to remove)
  // duration is in seconds (1-3)
  const handleTransitionSelect = (sceneId, position, transitionData) => {
    setTransitions(prev => {
      const newTransitions = { ...prev }
      const key = `${sceneId}-${position}`

      if (transitionData === null) {
        delete newTransitions[key]
      } else {
        // If just a string is passed (backward compatibility), convert to object
        if (typeof transitionData === 'string') {
          newTransitions[key] = { transition: transitionData, duration: 1 }
        } else {
          newTransitions[key] = transitionData
        }
      }
      return newTransitions
    })

    if (transitionData === null) {
      message.success('Transition removed')
    } else {
      const transitionName =
        typeof transitionData === 'string'
          ? transitionData
          : transitionData.transition
      message.success(`Transition updated to ${transitionName}`)
    }
  }

  const handleApplyTransitionToAll = transitionData => {
    if (!transitionData) return

    const normalizedTransition =
      typeof transitionData === 'string'
        ? { transition: transitionData, duration: 1 }
        : transitionData

    setTransitions(() => {
      const allTransitions = {}
      const firstSceneId = scenes?.[0]?.id

      ;(scenes || []).forEach(scene => {
        if (!scene?.id) return

        if (scene.id === firstSceneId) {
          allTransitions[`${scene.id}-start`] = normalizedTransition
        }

        const shots = scene.shots || []
        shots.forEach((shot, index) => {
          const position = index === shots.length - 1 ? 'end' : shot.id
          allTransitions[`${scene.id}-${position}`] = normalizedTransition
        })
      })

      return allTransitions
    })

    message.success(
      `Transition "${normalizedTransition.transition}" applied to all`
    )
  }

  const handleRemoveAllTransitions = () => {
    setTransitions({})
    message.success('Removed transitions from all')
  }

  // ===== UPLOAD HANDLERS =====
  const handleUploadVideo = (sceneId, shotId, file) => {
    if (!file) return

    let videoUrl
    if (typeof file === 'string') {
      videoUrl = file
    } else if (file?.type?.includes('video/')) {
      videoUrl = URL.createObjectURL(file)
    } else {
      message.error('Please upload a valid video file')
      return
    }

    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId ? { ...shot, vidSrc: videoUrl } : shot
            )
          }
        }
        return scene
      })
    )

    // Invalidate scene preview when a video is uploaded
    invalidateScenePreview(sceneId)
    message.success('Video uploaded successfully!')
  }

  const handleUploadImage = file => {
    if (!file) return false
    if (!file.type.includes('image/')) {
      message.error('Please upload an image file')
      return false
    }

    const imageUrl = URL.createObjectURL(file)
    updateAdvancedSettings('imageSrc', imageUrl)
    message.success('Image uploaded successfully!')
    return false
  }

  const handleSortEnd = (sceneId, { oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return
    setScenes(prev =>
      prev.map(scene =>
        scene.id === sceneId
          ? { ...scene, shots: arrayMove(scene.shots, oldIndex, newIndex) }
          : scene
      )
    )
    // Invalidate scene preview when shots are reordered
    invalidateScenePreview(sceneId)
  }

  const handleAllRegeneration = () => {
    Modal.confirm({
      title: 'Do you want to generate all videos?',
      content: 'This will overwrite all the current videos.',
      centered: true,
      onOk() {
        handleGenerateFullVideo()
      }
    })
  }

  const handleDeleteShot = (sceneId, shotId) => {
    const scene = scenes.find(scene => scene.id === sceneId)
    if (!scene) return

    const performDelete = () => {
      setScenes(prev =>
        prev.map(scene =>
          scene.id === sceneId
            ? { ...scene, shots: scene.shots.filter(s => s.id !== shotId) }
            : scene
        )
      )
      message.success('Shot deleted')
    }

    if (scene.shots.length === 1) {
      Modal.confirm({
        title: 'Delete Last Shot & Scene',
        content:
          'This is the last shot in the scene. Deleting it will remove the entire scene. Are you sure you want to proceed?',
        onOk: () => {
          setScenes(prev => prev.filter(s => s.id !== sceneId))
          message.success('Scene deleted')
        },
        centered: true
      })
      return
    }

    Modal.confirm({
      title: 'Delete Shot',
      content: 'Are you sure you want to delete this shot?',
      onOk: performDelete,
      centered: true
    })
  }

  // ===== EFFECTS =====
  // Local duration calculation logic is now handled by hooks
  // Auto-generation logic is handled by useAutoVideoGeneration hook

  // Sync cache to formData for persistence
  useEffect(() => {
    if (Object.keys(scenePreviewCache).length > 0) {
      setFormData(prev => ({
        ...prev,
        scenePreviewCache
      }))
    }
  }, [scenePreviewCache])

  // Sync transitions to formData for persistence
  useEffect(() => {
    if (transitions && !_.isEqual(transitions, formData.transitions)) {
      setFormData(prev => ({
        ...prev,
        transitions
      }))
    }
  }, [transitions, formData.transitions])

  // Initialize cache from formData on mount
  useEffect(() => {
    if (formData?.scenePreviewCache) {
      setScenePreviewCache(formData.scenePreviewCache)
    }
  }, [])

  // ===== POLLING EFFECT FOR FINAL VIDEO =====
  // Track if we were generating to auto-open modal on completion
  const wasGeneratingRef = useRef(false)
  useEffect(() => {
    if (isGeneratingFinalVideo) {
      wasGeneratingRef.current = true
    } else if (wasGeneratingRef.current) {
      wasGeneratingRef.current = false
      // Generation finished (either success or fail)
      setShowMainVideo(true)
    }
  }, [isGeneratingFinalVideo])

  useImperativeHandle(ref, () => ({
    openVideoEditor: () => {
      setShowSelectionStep(true)
    }
  }))

  // ===== COMPUTED VALUES =====
  const generatingShotKeySet = useMemo(() => {
    return new Set(
      (videoGenerating || []).map(item => `${item.sceneId}::${item.shotId}`)
    )
  }, [videoGenerating])

  const generationStats = useMemo(() => {
    let total = 0
    let completed = 0
    let failed = 0
    let generating = 0

    scenes.forEach(scene => {
      ;(scene.shots || []).forEach(shot => {
        total += 1
        const key = `${scene.id}::${shot.id}`
        const isShotLoading = generatingShotKeySet.has(key)

        if (isShotLoading) {
          generating += 1
          return
        }
        if (shot?.vidSrc || (shot?.is_static && shot?.imageSrc)) {
          completed += 1
          return
        }
        if (shot?.errorGeneratingVideo) {
          failed += 1
        }
      })
    })

    const pending = Math.max(total - completed - failed - generating, 0)
    const processed = completed + failed
    const progressPercent =
      total > 0 ? Math.round((processed / total) * 100) : 0

    let state = 'pending'
    if (total === 0) {
      state = 'empty'
    } else if (generating > 0 || generatingVideoForAllScenes) {
      state = 'generating'
    } else if (failed > 0) {
      state = 'attention'
    } else if (completed === total) {
      state = 'complete'
    }

    return {
      total,
      completed,
      failed,
      pending,
      generating,
      processed,
      progressPercent,
      state
    }
  }, [scenes, generatingShotKeySet, generatingVideoForAllScenes])

  const hasFailedVideos = generationStats.failed > 0
  const hasAnyShots = generationStats.total > 0
  const hasDownloadableClips = scenes.some(scene =>
    (scene.shots || []).some(shot => shot?.vidSrc)
  )
  const isAllScenesGenerated =
    hasAnyShots &&
    scenes.every(scene =>
      scene.shots.every(
        shot => shot.vidSrc || (shot.is_static && shot.imageSrc)
      )
    )

  const isAnyVideoGenerating =
    generationStats.generating > 0 || generatingVideoForAllScenes

  const generationStatusExtra = useMemo(
    () => <GenerationSummary stats={generationStats} />,
    [generationStats]
  )

  // ===== RENDER MODALS =====

  // Scene Preview Status (Loading/Error)
  if (showScenePreview && (scenePreviewError || !previewSceneVideo)) {
    return (
      <GenerationStatusModal
        visible={showScenePreview}
        status={scenePreviewError ? 'error' : 'loading'}
        title={scenePreviewError ? 'Preview Failed' : 'Generating Preview...'}
        subTitle={
          !scenePreviewError &&
          'We are assembling your scene shots. Please wait. This may take a few minutes.'
        }
        error={scenePreviewError}
        small
        onClose={() => {
          if (scenePreviewError) {
            setShowScenePreview(false)
            setScenePreviewError(null)
          } else {
            Modal.confirm({
              title: 'Generation in progress',
              content:
                'Video generation is still running in the background. Are you sure you want to close this status window?',
              okText: 'Yes, Close',
              cancelText: 'Stay',
              centered: true,
              onOk: () => {
                setShowScenePreview(false)
                setScenePreviewError(null)
              }
            })
          }
        }}
        onRetry={() => {
          const lastSceneId = Object.keys(scenePreviewCache).find(id => {
            return false
          })
        }}
      />
    )
  }

  // Scene Preview Video Modal
  else if (showScenePreview && previewSceneVideo) {
    return (
      <AIVideoModal
        visible={showScenePreview}
        videoUrl={previewSceneVideo}
        title="Scene Preview"
        onClose={() => {
          setShowScenePreview(false)
          setPreviewSceneVideo(null)
          setScenePreviewError(null)
        }}
      />
    )
  }

  // Final Video Status (Error)
  else if (showMainVideo && (finalVideoError || !formData?.finalVideo)) {
    return (
      <GenerationStatusModal
        visible={showMainVideo}
        small
        status="error"
        title="Generation Failed"
        subTitle="Something went wrong while creating your final video."
        error={finalVideoError}
        onClose={() => setShowMainVideo(false)}
        onRetry={() => {
          setShowMainVideo(false)
          setShowVideoEditor(true)
        }}
      />
    )
  } else if (showMainVideo && formData.finalVideo) {
    return (
      <AIVideoModal
        visible={showMainVideo}
        videoUrl={formData.finalVideo}
        title="Your AI Generated Video"
        onClose={() => setShowMainVideo(false)}
      />
    )
  }

  // ===== MAIN RENDER =====
  else if (isCalculatingDurations) {
    return (
      <div className="video-generation-container">
        <StatusView
          status="loading"
          title="Calculating Durations..."
          subTitle="We're calculating scene durations and finalizing your video structure. This ensures everything is perfectly timed before you start generating."
          // progress={calculationProgress}
          inline
        />
      </div>
    )
  }

  return (
    <div className="video-generation-container">
      <div className="video-generation-header">
        <div className="video-generation-header__title">
          <StepTitle
            iconType="video-camera"
            title="Video Generation"
            description="Create videos for your project"
            showDivider={false}
          />
          <div className="video-generation-header__summary">
            {generationStatusExtra}
          </div>
        </div>
        <div className="video-generation-header__actions">
          <div
            className="view-mode-toggle"
            role="group"
            aria-label="Shot view mode"
          >
            <Button
              size="small"
              className={`view-mode-btn ${
                shotViewMode === 'expanded' ? 'is-active' : ''
              }`}
              onClick={() => setShotViewMode('expanded')}
            >
              <Icon type="appstore" />
              Expanded
            </Button>
            <Button
              size="small"
              className={`view-mode-btn ${
                shotViewMode === 'compact' ? 'is-active' : ''
              }`}
              onClick={() => setShotViewMode('compact')}
            >
              <Icon type="bars" />
              Compact
            </Button>
          </div>
          {hasFailedVideos && (
            <Button
              icon="reload"
              className="video-generation-header__action-btn video-generation-header__action-btn--danger"
              onClick={handleRetryFailedVideos}
              disabled={generatingVideoForAllScenes}
            >
              Retry Failed
            </Button>
          )}
          {isAllScenesGenerated && (
            <Button
              icon="sync"
              className="video-generation-header__action-btn video-generation-header__action-btn--primary"
              loading={generatingVideoForAllScenes}
              onClick={handleAllRegeneration}
            >
              Regenerate All
            </Button>
          )}
          <Button
            icon="download"
            className="video-generation-header__action-btn"
            onClick={() => setShowBulkDownloadModal(true)}
            disabled={!hasDownloadableClips}
          >
            Download Clips
          </Button>
        </div>
      </div>

      {scenes.map((scene, index) => (
        <Fragment key={scene.id + 'scene' + index}>
          <Scene
            key={scene.id}
            scene={scene}
            transitions={transitions}
            formData={formData}
            isGeneratingAll={generatingVideoForAllScenes}
            videoGenerating={videoGenerating}
            setScenes={setScenes}
            handleRegenerateVideo={handleRegenerateVideo}
            handleStopVideoGeneration={handleStopVideoGeneration}
            handleUploadVideo={handleUploadVideo}
            handleEditSettings={handleEditSettings}
            handleTransitionSelect={handleTransitionSelect}
            handleApplyTransitionToAll={handleApplyTransitionToAll}
            handleRemoveAllTransitions={handleRemoveAllTransitions}
            videoProvider={videoProvider}
            onSortEnd={params => handleSortEnd(scene.id, params)}
            onPreviewScene={handlePreviewScene}
            updateShotDataRef={updateShotDataRef}
            handleDeleteShot={handleDeleteShot}
            // Image generation props
            handleRegenerateImage={handleRegenerateImage}
            handleRegenerateWithReferenceNanoBanana={
              handleRegenerateWithReferenceNanoBanana
            }
            isImageGenerationLoading={isImageGenerationLoading}
            videoId={videoId}
            isFetchingCharacters={isFetchingCharacters}
            shotViewMode={shotViewMode}
          />
        </Fragment>
      ))}

      <FinalVideoSelection
        visible={showSelectionStep}
        scenes={scenes}
        onCancel={() => setShowSelectionStep(false)}
        onConfirm={filteredScenes => {
          setSelectedScenesForEditor(filteredScenes)
          setShowSelectionStep(false)
          setShowVideoEditor(true)
        }}
      />

      <VideoEditorModal
        isSingleScene={editorScenes?.length === 1}
        visible={showVideoEditor}
        scenes={
          editorScenes?.length > 0
            ? editorScenes
            : selectedScenesForEditor.length > 0
            ? selectedScenesForEditor
            : scenes
        }
        transitions={
          editorTransitions && Object.keys(editorTransitions).length > 0
            ? editorTransitions
            : transitions
        }
        onConfirm={handleGenerateFinalVideoWrapper}
        onCancel={() => {
          setShowVideoEditor(false)
          setEditorScenes([])
          setEditorTransitions({})
        }}
        finalVideoExists={formData?.finalVideo}
      />

      {/* Settings Drawer */}
      <Drawer
        title="Shot Editor"
        placement="right"
        closable={true}
        onClose={() => {
          setEditingSettings(null)
          setDrawerVisible(false)
        }}
        visible={drawerVisible}
        width={400}
      >
        {editingSettings &&
          (() => {
            const scene = scenes.find(s => s.id === editingSettings.sceneId)
            const shot = scene?.shots.find(
              sh => sh.id === editingSettings.shotId
            )
            if (!shot) return null
            const settings = {
              ...shot,
              sceneId: editingSettings.sceneId,
              shotId: editingSettings.shotId
            }
            return (
              <SettingsContent
                settings={settings}
                updateSettings={updateSettings}
                handleAdvancedSettings={handleAdvancedSettings}
                saveSettings={saveSettings}
                formData={formData}
                videoProvider={videoProvider}
                isVideoGenerating={videoGenerating.some(
                  v =>
                    v.sceneId === editingSettings.sceneId &&
                    v.shotId === editingSettings.shotId
                )}
              />
            )
          })()}
      </Drawer>

      {/* Advanced Settings Drawer */}
      <Drawer
        title="Advanced Shot Settings"
        placement="right"
        closable={true}
        onClose={() => setAdvancedSettingsVisible(false)}
        visible={advancedSettingsVisible}
        width={520}
        zIndex={1001}
      >
        {advancedSettings && (
          <AdvancedSettingsContent
            settings={advancedSettings}
            updateSettings={updateAdvancedSettings}
            saveSettings={saveAdvancedSettings}
            handleUploadImage={handleUploadImage}
            formData={formData}
            onCancel={() => setAdvancedSettingsVisible(false)}
          />
        )}
      </Drawer>

      <RegenerationInfoModal
        visible={showRegenerationInfo}
        onClose={() => setShowRegenerationInfo(false)}
        affectedScenes={affectedScenesByMismatch}
      />

      <BulkClipDownloadModal
        visible={showBulkDownloadModal}
        scenes={scenes}
        videoId={videoId}
        onClose={() => setShowBulkDownloadModal(false)}
      />

      <ImageEditorModal
        videoId={videoId}
        visible={showImageEditorModal}
        onCancel={() => {
          setShowImageEditorModal(false)
          setCurrentEditingShot(null)
        }}
        onConfirm={handleImageEditorConfirm}
        mode={imageEditorMode}
        {...(() => {
          if (!currentEditingShot) return {}
          const scene = scenes.find(s => s.id === currentEditingShot.sceneId)
          const shot = scene?.shots.find(
            sh => sh.id === currentEditingShot.shotId
          )
          if (!shot) return {}

          // Gather all available images from the entire project as references
          const allProjectImages = scenes.flatMap(s =>
            (s.shots || []).map(sh => ({
              url: sh.imageSrc,
              shotTitle: `Scene ${s.id} - Shot ${sh.id}`,
              sceneTitle: `Scene ${s.id}`
            }))
          )

          // Add location map and characters as references
          const locationRefs = []
          if (formData.locationReference) {
            Object.entries(formData.locationReference).forEach(
              ([key, value]) => {
                const url =
                  typeof value === 'string'
                    ? value
                    : value?.uri || value?.url || value?.image || value?.src
                if (url) {
                  locationRefs.push({
                    url: url,
                    shotTitle: key,
                    sceneTitle: 'Location'
                  })
                }
              }
            )
          }

          const characterRefs = []
          if (formData.characterList) {
            formData.characterList.forEach(char => {
              const url = char.attributes?.profile_pic
              if (url) {
                characterRefs.push({
                  url: url,
                  shotTitle: char.name,
                  sceneTitle: 'Character'
                })
              }
            })
          }

          return {
            initialPrompt: shot.initial_image_prompt,
            currentImage: shot.imageSrc,
            aspectRatio: formData.aspectRatio, // Corrected from ratio
            referenceImages: [
              ...allProjectImages,
              ...locationRefs,
              ...characterRefs
            ].filter(img => img.url)
          }
        })()}
      />
    </div>
  )
})

export default VideoGeneration
